import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { useQueryClient } from '@tanstack/react-query';
import { colors } from '@tabbio/tokens';
import { evaluatePassword, passwordSchema } from '@tabbio/utils';
import { PasswordField } from '../common/PasswordField';
import { useTranslation } from 'react-i18next';
import { AUTH_LINKED_ACCOUNTS_QUERY_KEY, useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useFont } from '../../providers/FontProvider';
import { setAccountPassword } from '../../lib/account-actions';
import { markPostOnboardingWelcomePending } from '../../lib/post-onboarding-welcome';
import { NativeButton } from '../common/NativeButton';

type Props = {
  email?: string | null;
  onComplete: () => void;
};

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export function PasswordBootstrapForm({ email, onComplete }: Props) {
  const { theme } = useTheme();
  const { t } = useTranslation('auth');
  const { fonts } = useFont();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const parsedPassword = passwordSchema.safeParse(password);
    if (!parsedPassword.success) {
      setError(
        parsedPassword.error.errors[0]?.message ||
          t('login.passwordValidationError'),
      );
      return;
    }

    if (password !== confirmPassword) {
      setError(t('login.bootstrapPasswordMismatch'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const session = await setAccountPassword(password);

      if (session.user.role !== 'employer') {
        markPostOnboardingWelcomePending(session.user.id);
      }

      const now = new Date().toISOString();
      queryClient.setQueriesData(
        { queryKey: AUTH_LINKED_ACCOUNTS_QUERY_KEY },
        (current) => {
          if (!Array.isArray(current)) {
            return current;
          }

          if (
            current.some(
              (account) =>
                account &&
                typeof account === 'object' &&
                'providerId' in account &&
                (account as { providerId?: string }).providerId === 'credential',
            )
          ) {
            return current;
          }

          return [
            ...current,
            {
              id: `bootstrap-credential-${user?.id ?? 'current'}`,
              providerId: 'credential',
              accountId: user?.email ?? email ?? 'credential',
              scopes: [],
              createdAt: now,
              updatedAt: now,
              userId: user?.id ?? '',
            },
          ];
        },
      );

      await queryClient.invalidateQueries({
        queryKey: AUTH_LINKED_ACCOUNTS_QUERY_KEY,
      });

      onComplete();
    } catch (submitError) {
      setError(
        getErrorMessage(submitError, t('login.bootstrapPasswordError')),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.section}>
      <Text style={[styles.title, { color: theme.text, fontFamily: fonts.medium }]}>
        {t('login.bootstrapPasswordTitle')}
      </Text>
      <Text style={[styles.subtitle, { color: theme.textSecondary, fontFamily: fonts.regular }]}>
        {t('login.bootstrapPasswordSubtitle', {
          email: email ?? user?.email ?? '',
        })}
      </Text>

      <PasswordField
        placeholder={t('login.bootstrapPasswordPlaceholder')}
        placeholderTextColor={theme.textTertiary}
        value={password}
        onChangeText={(value) => {
          setPassword(value);
          if (error) setError('');
        }}
        error={!!error}
        showRules
        autoComplete="new-password"
        textContentType="newPassword"
        autoFocus
        returnKeyType="next"
      />

      <PasswordField
        placeholder={t('login.bootstrapPasswordConfirmPlaceholder')}
        placeholderTextColor={theme.textTertiary}
        value={confirmPassword}
        onChangeText={(value) => {
          setConfirmPassword(value);
          if (error) setError('');
        }}
        error={!!error}
        autoComplete="new-password"
        textContentType="newPassword"
        returnKeyType="go"
        onSubmitEditing={handleSubmit}
      />

      {error ? (
        <Text style={[styles.error, { color: colors.danger }]}>{error}</Text>
      ) : null}

      <NativeButton
        title={t('login.bootstrapPasswordSubmit')}
        onPress={handleSubmit}
        variant="solid"
        size="lg"
        pill
        block
        loading={submitting}
        disabled={!evaluatePassword(password).valid || !confirmPassword.trim()}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: 12,
    width: '100%',
  },
  title: {
    fontSize: 32,
    fontWeight: '600',
    letterSpacing: -0.5,
  },
  subtitle: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 4,
  },
  error: {
    fontSize: 13,
    lineHeight: 18,
  },
});
